import { NavLink, useMatch } from "react-router-dom";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useGetLeagueQuery } from "../api/endpoints/leagueDetail";
import { useGetHandicapHistoryQuery } from "../api/endpoints/handicap";
import {
  useLeagueFacilityId,
  useListedLeague,
} from "../hooks/useLeagueFacilityId";
import { leagueIdFromPath } from "../utils/leagueIdFromPath";

export function LeaguesBreadcrumb() {
  const leagueDetailMatch = useMatch("/leagues/:leagueId");
  const roundDetailMatch = useMatch("/leagues/:leagueId/rounds/:roundId");
  const handicapMatch = useMatch(
    "/leagues/:leagueId/roster/:memberId/handicap-history",
  );
  const isDashboard = !!useMatch({ path: "/leagues", end: true });

  const rawLeagueId =
    handicapMatch?.params.leagueId ??
    roundDetailMatch?.params.leagueId ??
    leagueDetailMatch?.params.leagueId;
  const leagueId = leagueIdFromPath(rawLeagueId);
  const isNew = rawLeagueId === "new";
  const isRound = !!roundDetailMatch;
  const isHandicap = !!handicapMatch;
  const facilityId = useLeagueFacilityId(isHandicap ? leagueId : undefined);
  const { data: handicap } = useGetHandicapHistoryQuery(
    {
      facilityId: facilityId ?? "",
      leagueId: leagueId ?? "",
      memberId: handicapMatch?.params.memberId ?? "",
    },
    { skip: !isHandicap || !facilityId || !leagueId },
  );

  const { data: leagueData } = useGetLeagueQuery(leagueId ?? "", {
    skip: !leagueId,
  });
  const listed = useListedLeague(leagueId);

  const leagueName =
    leagueData?.leagueV2Detail?.name ||
    listed?.name ||
    handicap?.leagueName ||
    "League";
  const roundId = roundDetailMatch?.params.roundId;
  const round = leagueData?.rounds?.recentRounds?.find(
    (r) => r.roundId === roundId,
  );
  const roundLabel =
    round?.displayName ??
    (round?.roundNumber ? `Round ${round.roundNumber}` : "Round");

  const pageLabel = isDashboard
    ? "Leagues Dashboard"
    : isNew
      ? "New League"
      : isRound
        ? roundLabel
        : isHandicap
          ? handicap?.playerName
            ? `${handicap.playerName} · Handicap history`
            : "Handicap history"
          : leagueName;

  // Dynamic names (league / round) can be long; keep the breadcrumb on one line
  // and let the name crumbs truncate rather than wrap the whole bar.
  const nameCrumb =
    "block max-w-[8rem] truncate sm:max-w-[12rem] lg:max-w-[18rem]";

  return (
    <>
      {!isDashboard && (
        <>
          <BreadcrumbItem className="hidden shrink-0 md:block">
            <BreadcrumbLink
              className="whitespace-nowrap"
              render={<NavLink to="/leagues" />}
            >
              Leagues Dashboard
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator className="hidden md:block" />
        </>
      )}
      {(isRound || isHandicap) && (
        <>
          <BreadcrumbItem className="hidden min-w-0 md:block">
            <BreadcrumbLink
              className={nameCrumb}
              title={leagueName}
              render={<NavLink to={`/leagues/${leagueId}`} />}
            >
              {leagueName}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator className="hidden md:block" />
        </>
      )}
      <BreadcrumbItem className="min-w-0">
        <BreadcrumbPage className={nameCrumb} title={pageLabel}>
          {pageLabel}
        </BreadcrumbPage>
      </BreadcrumbItem>
    </>
  );
}
